import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';
import Modal from '../components/Modal';
import YesPlacementForm from '../components/YesPlacementForm';
import '../styles/DashboardPage.scss';
import '../styles/Yes4Youth.scss';

const TABS = ['Programme Overview', 'Youth Placements', 'Absorption Rate'];

const fmtPct = (n) => `${Math.round((n || 0) * 100 * 10) / 10}%`;

const RECOGNITION_REASONS = {
  not_registered: 'Register as a Y.E.S Non-Profit Company above to become eligible for recognition.',
  prior_level_not_maintained: 'Your B-BBEE level has dropped compared to the level recorded on Company Info for this financial year — recognition can\'t be awarded until it\'s maintained or improved.',
  no_target: 'A youth placement target couldn\'t be worked out yet — check your headcount, profit and revenue figures are up to date.',
  below_lowest_tier: 'You haven\'t yet reached enough placements or absorption to earn recognition.',
  tier_applied: 'Recognition earned for this financial year.',
};

const WARNING_MESSAGES = {
  absorption_not_yet_verifiable: 'None of your placements have finished their programme yet, so absorption can\'t be checked — this won\'t hold back your recognition for now.',
};

const QUALIFICATION_MODE_LABELS = {
  each: 'every priority element individually met the requirement',
  average: 'the average across your priority elements met the requirement',
  n_of_m: 'Ownership plus one other priority element met the requirement',
  always_qualifies: 'automatically qualified (Transport exemption)',
};

const ELEMENT_LABELS = {
  ownership: 'Ownership',
  skills_development: 'Skills Development',
  esd: 'Enterprise & Supplier Development',
};

const TARGET_BASIS_LABELS = {
  qse_headcount_table: 'your employee headcount',
  headcount: 'your employee headcount',
  revenue_table: 'your annual revenue',
  npat: 'your average profit (NPAT)',
};

const PLACEMENT_STATUS_LABELS = {
  in_progress: 'In Progress',
  absorbed: 'Absorbed',
  not_absorbed: 'Not Absorbed',
  exited_early: 'Exited Early',
};

const Yes4Youth = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState(0);

  const [summary, setSummary] = useState(null);
  const [summaryLoading, setSummaryLoading] = useState(true);
  const [registeredSaving, setRegisteredSaving] = useState(false);
  const [recognition, setRecognition] = useState(null);

  const [placements, setPlacements] = useState([]);
  const [placementsLoading, setPlacementsLoading] = useState(true);

  const [formOpen, setFormOpen] = useState(false);
  const [editingPlacement, setEditingPlacement] = useState(null);
  const [formSaving, setFormSaving] = useState(false);

  const loadSummary = useCallback(() => {
    setSummaryLoading(true);
    api.get('/core/yes4youth/summary/')
      .then((res) => setSummary(res.data))
      .catch(() => setSummary({ not_applicable: false, error: 'Unable to load Y.E.S summary.' }))
      .finally(() => setSummaryLoading(false));
  }, []);

  const loadPlacements = useCallback(() => {
    setPlacementsLoading(true);
    api.get('/core/yes4youth/placements/')
      .then((res) => setPlacements(res.data || []))
      .catch(() => setPlacements([]))
      .finally(() => setPlacementsLoading(false));
  }, []);

  // The actual recognition outcome (qualifies_for_recognition, reason, tier...) only comes back
  // from combined-total's `yes` block — /yes4youth/summary/ never calls _apply_yes_recognition.
  const loadRecognition = useCallback(() => {
    api.get('/core/scorecard/combined-total/')
      .then((res) => setRecognition(res.data?.yes || null))
      .catch(() => setRecognition(null));
  }, []);

  useEffect(() => {
    loadSummary();
    loadPlacements();
    loadRecognition();
  }, [loadSummary, loadPlacements, loadRecognition]);

  const toggleRegistration = () => {
    if (!summary) return;
    setRegisteredSaving(true);
    const next = !summary.registered;
    api.post('/core/yes4youth/registration/save/', {
      ...summary.registration,
      registered: next,
    })
      .then(() => { loadSummary(); loadRecognition(); })
      .finally(() => setRegisteredSaving(false));
  };

  const openAddForm = () => {
    setEditingPlacement(null);
    setFormOpen(true);
  };

  const openEditForm = (placement) => {
    setEditingPlacement(placement);
    setFormOpen(true);
  };

  const handleSavePlacement = (form) => {
    setFormSaving(true);
    const req = editingPlacement
      ? api.patch(`/core/yes4youth/placements/${editingPlacement._id}/`, form)
      : api.post('/core/yes4youth/placements/', form);

    req
      .then(() => {
        setFormOpen(false);
        setEditingPlacement(null);
        loadPlacements();
        loadSummary();
      })
      .catch((err) => {
        // eslint-disable-next-line no-alert
        alert(err.response?.data?.error || 'Failed to save placement.');
      })
      .finally(() => setFormSaving(false));
  };

  const handleDeletePlacement = (placement) => {
    // eslint-disable-next-line no-alert
    if (!window.confirm(`Remove ${placement.first_name} ${placement.last_name}?`)) return;
    api.delete(`/core/yes4youth/placements/${placement._id}/`)
      .then(() => {
        loadPlacements();
        loadSummary();
      });
  };

  // Mirrors the backend's calculate_yes_actuals: any captured placement (with a start_date) is
  // eligible immediately for the FY it's captured under — no duration wait, and the
  // black-youth/unemployed/new-position fields are no longer gating checks, kept on the record
  // for display only.
  const isEligible = (p) => !!p.start_date;

  return (
    <div className="dp-container">
      <div className="dp-header">
        <button className="dp-back" onClick={() => navigate('/')}>← Back</button>
        <div className="dp-title">
          <h1>Y.E.S — Youth Employment Service</h1>
          <p>Programme tracking, qualification and placements</p>
        </div>
      </div>

      <div className="yes-body">
        <div className="yes-tabs" role="tablist">
          {TABS.map((label, i) => (
            <button
              key={label}
              role="tab"
              aria-selected={i === activeTab}
              className={`yes-tab${i === activeTab ? ' active' : ''}`}
              onClick={() => setActiveTab(i)}
            >
              {label}
            </button>
          ))}
        </div>

        <div className="yes-panel">
          {summaryLoading ? (
            <div className="yes-loading">Loading…</div>
          ) : summary?.not_applicable ? (
            <div className="yes-empty">
              Y.E.S is not available for your current entity size/sector.
            </div>
          ) : summary?.error ? (
            <div className="yes-empty yes-empty--error">{summary.error}</div>
          ) : (
            <>
              {activeTab === 0 && (
                <ProgrammeOverview
                  summary={summary}
                  recognition={recognition}
                  onToggleRegistration={toggleRegistration}
                  registeredSaving={registeredSaving}
                />
              )}
              {activeTab === 1 && (
                <YouthPlacements
                  placements={placements}
                  loading={placementsLoading}
                  onAdd={openAddForm}
                  onEdit={openEditForm}
                  onDelete={handleDeletePlacement}
                  isEligible={isEligible}
                />
              )}
              {activeTab === 2 && (
                <AbsorptionRate summary={summary} placements={placements} isEligible={isEligible} />
              )}
            </>
          )}
        </div>
      </div>

      <Modal
        isOpen={formOpen}
        onClose={() => { setFormOpen(false); setEditingPlacement(null); }}
        title={editingPlacement ? 'Edit Placement' : 'Add Placement'}
        size="lg"
      >
        <YesPlacementForm
          placement={editingPlacement}
          onSave={handleSavePlacement}
          onCancel={() => { setFormOpen(false); setEditingPlacement(null); }}
          saving={formSaving}
        />
      </Modal>
    </div>
  );
};

const ProgrammeOverview = ({ summary, recognition, onToggleRegistration, registeredSaving }) => {
  const qual = summary.qualification || {};
  const target = summary.target || {};
  const actuals = summary.actuals || {};
  const progress = target.target ? Math.min(100, ((actuals.eligible_placements || 0) / target.target) * 100) : 0;

  return (
    <div className="yes-overview">
      <div className="yes-card">
        <div className="yes-card-head">
          <h3>Y.E.S Registration</h3>
          <button
            className={`yes-toggle${summary.registered ? ' yes-toggle--on' : ''}`}
            onClick={onToggleRegistration}
            disabled={registeredSaving}
            title={summary.registered ? 'Click to mark as not registered' : 'Click here if registered'}
          >
            <span className="yes-toggle-dot" aria-hidden="true">{summary.registered ? '✓' : ''}</span>
            {registeredSaving
              ? 'Saving…'
              : summary.registered
                ? 'Registered — click to undo'
                : 'Click here if registered'}
          </button>
        </div>
        <p className="yes-card-note">
          Registration as a Y.E.S Non-Profit Company is required before any B-BBEE level uplift or bonus
          points can be applied. The "BEE Level Achieved" recorded on this financial year's Company Info
          (the certificate currently on file) determines whether the prior level was maintained.
        </p>
      </div>

      <div className="yes-card">
        <h3>Recognition Status</h3>
        {recognition ? (
          <>
            <div className={`yes-qual-verdict${recognition.qualifies_for_recognition ? ' pass' : ' fail'}`}>
              {recognition.qualifies_for_recognition ? 'Y.E.S Recognition Awarded' : 'Y.E.S Recognition Not Awarded'}
            </div>
            <p className="yes-card-note">
              {RECOGNITION_REASONS[recognition.reason] || recognition.reason}
            </p>
            {recognition.qualifies_for_recognition && (
              <p className="yes-card-note">
                {`+${recognition.levels_gained} level${recognition.levels_gained !== 1 ? 's' : ''}`}
                {recognition.bonus_points_gained > 0 ? ` and +${recognition.bonus_points_gained} bonus points` : ''}
              </p>
            )}
            {(recognition.warnings || []).map((w) => (
              <p key={w} className="yes-card-note yes-card-note--warning">{WARNING_MESSAGES[w] || w}</p>
            ))}
          </>
        ) : (
          <p className="yes-card-note">Recognition status unavailable.</p>
        )}
      </div>

      <div className="yes-card">
        <h3>Qualification</h3>
        <p className="yes-card-note">
          Whether your company's scorecard scores are strong enough to qualify for Y.E.S recognition at all.
        </p>
        {qual.mode_passed === 'always_qualifies' ? (
          <div className="yes-qual-auto">Automatically qualified (Transport exemption)</div>
        ) : (
          <>
            <div className={`yes-qual-verdict${qual.qualifies ? ' pass' : ' fail'}`}>
              {qual.qualifies ? 'Qualifies' : 'Does not qualify'}
              {qual.mode_passed && QUALIFICATION_MODE_LABELS[qual.mode_passed]
                ? ` — ${QUALIFICATION_MODE_LABELS[qual.mode_passed]}`
                : ''}
            </div>
            <table className="yes-table">
              <thead>
                <tr><th>Element</th><th>Score</th><th>Out of</th><th>%</th><th>Meets requirement?</th></tr>
              </thead>
              <tbody>
                {(qual.elements || []).map((el) => (
                  <tr key={el.element}>
                    <td>{ELEMENT_LABELS[el.element] || el.element}</td>
                    <td>{el.actual_points}</td>
                    <td>{el.max_points}</td>
                    <td>{fmtPct(el.pct)}</td>
                    <td>{el.passed_each ? 'Yes' : 'No'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </>
        )}
      </div>

      <div className="yes-card">
        <h3>Youth Placement Target</h3>
        <div className="yes-target-row">
          <div><span className="yes-target-label">Youth Needed</span><span className="yes-target-value">{target.target ?? '—'}</span></div>
          <div><span className="yes-target-label">Youth Placed</span><span className="yes-target-value">{actuals.eligible_placements ?? 0}</span></div>
          <div><span className="yes-target-label">Absorbed Into Jobs</span><span className="yes-target-value">{fmtPct((actuals.absorption_pct || 0) / 100)}</span></div>
        </div>
        {target.basis && (
          <div className="yes-card-note">
            Calculated from {TARGET_BASIS_LABELS[target.basis] || target.basis.replace(/_/g, ' ')}.
          </div>
        )}
        <div className="yes-progress"><div className="yes-progress-fill" style={{ width: `${progress}%` }} /></div>
        <p className="yes-card-note">
          {`${actuals.eligible_placements ?? 0} of ${target.target ?? '?'} youth placed toward your target.`}
        </p>
        <p className="yes-card-note">
          "Absorbed Into Jobs" tracks placements that have been kept on permanently after their programme
          ended — this isn't checked for the very first year a company takes part in Y.E.S.
        </p>
      </div>
    </div>
  );
};

const YouthPlacements = ({ placements, loading, onAdd, onEdit, onDelete, isEligible }) => (
  <div className="yes-placements">
    <div className="yes-placements-head">
      <h3>Youth Placements ({placements.length})</h3>
      <button className="yes-btn yes-btn--primary" onClick={onAdd}>+ Add Placement</button>
    </div>
    {loading ? (
      <div className="yes-loading">Loading…</div>
    ) : placements.length === 0 ? (
      <div className="yes-empty">No placements recorded yet.</div>
    ) : (
      <div className="yes-table-wrap">
        <table className="yes-table">
          <thead>
            <tr>
              <th>Name</th><th>Start</th><th>Planned End</th><th>Status</th><th>Eligible</th><th></th>
            </tr>
          </thead>
          <tbody>
            {placements.map((p) => (
              <tr key={p._id}>
                <td>{p.first_name} {p.last_name}</td>
                <td>{p.start_date}</td>
                <td>{p.planned_end_date}</td>
                <td>{PLACEMENT_STATUS_LABELS[p.absorption_status] || p.absorption_status}</td>
                <td>{isEligible(p) ? '✔' : '—'}</td>
                <td className="yes-row-actions">
                  <button className="yes-link" onClick={() => onEdit(p)}>Edit</button>
                  <button className="yes-link yes-link--danger" onClick={() => onDelete(p)}>Delete</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    )}
  </div>
);

const AbsorptionRate = ({ summary, placements, isEligible }) => {
  const actuals = summary?.actuals || {};
  const eligible = placements.filter(isEligible);
  const counts = eligible.reduce((acc, p) => {
    acc[p.absorption_status] = (acc[p.absorption_status] || 0) + 1;
    return acc;
  }, {});

  return (
    <div className="yes-absorption">
      <div className="yes-card">
        <h3>Absorption Summary</h3>
        <p className="yes-card-note">
          "Absorbed" placements are youth who were kept on permanently after their 12-month programme finished.
        </p>
        <div className="yes-target-row">
          <div><span className="yes-target-label">Youth Placed</span><span className="yes-target-value">{actuals.eligible_placements ?? 0}</span></div>
          <div><span className="yes-target-label">Absorbed</span><span className="yes-target-value">{actuals.absorbed_placements ?? 0}</span></div>
          <div><span className="yes-target-label">Absorption Rate</span><span className="yes-target-value">{fmtPct((actuals.absorption_pct || 0) / 100)}</span></div>
        </div>
      </div>
      <div className="yes-card">
        <h3>Breakdown by Status</h3>
        <table className="yes-table">
          <thead><tr><th>Status</th><th>Count</th></tr></thead>
          <tbody>
            {['in_progress', 'absorbed', 'not_absorbed', 'exited_early'].map((status) => (
              <tr key={status}>
                <td>{PLACEMENT_STATUS_LABELS[status] || status}</td>
                <td>{counts[status] || 0}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default Yes4Youth;
